import { Card, Table } from "react-bootstrap";
import type { FinalData } from "../lib/explainTypes";
import { formatCurrency } from "../../../lib/numbers";

interface Props {
  final: FinalData;
}

const num = (s: string | undefined): number =>
  s == null || s === "" ? 0 : Number(s);

const asPercent = (s: string | undefined, decimals = 2): string =>
  s == null ? "—" : `${(num(s) * 100).toFixed(decimals)}%`;

const asSignedPercent = (s: string | undefined, decimals = 2): string => {
  if (s == null) return "—";
  const v = num(s) * 100;
  return `${v > 0 ? "+" : ""}${v.toFixed(decimals)}%`;
};

const formatShares = (s: string | undefined): string =>
  num(s).toLocaleString(undefined, { maximumFractionDigits: 4 });

// Reads `response.final`. Per architecture spec §3.3.
//
// Trade totals (`buy_total`/`sell_total`/`trade_count`) and realized-gain
// rollups depend on backend fixes in `v3_final_from_native` -- see
// APP-1241-final-rollup-gaps.md in the python repo. Until those land
// the values may be zero; rendering still shows the row so the gap is
// obvious rather than silently absent.
export default function FinalPanel({ final }: Props) {
  const buy = num(final.trade.buy_total);
  const sell = num(final.trade.sell_total);
  const net = buy - sell;
  const balance = num(final.balance);

  const realizedSt = final.trade.realized_gain_st_total;
  const realizedLt = final.trade.realized_gain_lt_total;
  const realizedTotal = num(realizedSt) + num(realizedLt);
  // Hide the row entirely when the rollup is zero -- common for
  // all-IRA / all-tax-deferred households where the row would only
  // ever read $0 and add noise.
  const hasRealized =
    (realizedSt != null || realizedLt != null) && realizedTotal !== 0;

  const riskKeys = Array.from(
    new Set([
      ...Object.keys(final.target_risk_weights),
      ...Object.keys(final.achieved_risk_weights),
    ]),
  );

  return (
    <Card className="mb-3">
      <Card.Header>Final Outcome</Card.Header>
      <Card.Body>
        <Table size="sm" responsive className="mb-3">
          <tbody>
            <tr>
              <th>AUM Total</th>
              <td className="text-end">{formatCurrency(balance)}</td>
            </tr>
            <tr>
              <th>Sell Total</th>
              <td className="text-end">{formatCurrency(sell)}</td>
            </tr>
            <tr>
              <th>Buy Total</th>
              <td className="text-end">{formatCurrency(buy)}</td>
            </tr>
            <tr>
              <th>Net</th>
              <td
                className={`text-end ${net > 0 ? "text-success" : net < 0 ? "text-danger" : ""}`}
              >
                {net > 0 ? "+" : ""}
                {formatCurrency(net)}
              </td>
            </tr>
            <tr>
              <th>Trade Shares (net)</th>
              <td className="text-end">
                {formatShares(final.trade.shares_net)}
              </td>
            </tr>
            <tr>
              <th>Trade Count</th>
              <td className="text-end">
                {final.trade.trade_count.toLocaleString()}
              </td>
            </tr>
            {hasRealized && (
              <tr>
                <th>Realized G/L Total</th>
                <td className="text-end">{formatCurrency(realizedTotal)}</td>
              </tr>
            )}
            <tr>
              <th>Within Risk Threshold</th>
              <td className="text-end">
                {final.within_risk_threshold ? "Yes" : "No"}
              </td>
            </tr>
            <tr>
              <th>Within Asset Class Threshold</th>
              <td className="text-end">
                {final.within_asset_class_threshold ? "Yes" : "No"}
              </td>
            </tr>
          </tbody>
        </Table>

        {riskKeys.length > 0 && (
          <Table size="sm">
            <thead>
              <tr>
                <th>Risk class</th>
                <th className="text-end">Target</th>
                <th className="text-end">Achieved</th>
                <th className="text-end">Delta</th>
              </tr>
            </thead>
            <tbody>
              {riskKeys.map((k) => (
                <tr key={k}>
                  <td>{k}</td>
                  <td className="text-end">
                    {asPercent(final.target_risk_weights[k])}
                  </td>
                  <td className="text-end">
                    {asPercent(final.achieved_risk_weights[k])}
                  </td>
                  <td className="text-end">
                    {asSignedPercent(final.risk_deltas[k])}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {final.shortfall_reasons.length > 0 && (
          <div className="mt-3">
            <strong>Shortfall reasons:</strong>
            <ul>
              {final.shortfall_reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </div>
        )}

        {final.limiting_constraints.length > 0 && (
          <div className="mt-3">
            <strong>Limiting constraints:</strong>{" "}
            {final.limiting_constraints.join(", ")}
          </div>
        )}

        {final.wash_sale_considerations.length > 0 && (
          <div className="mt-3">
            <strong>Wash sales:</strong>
            <ul>
              {final.wash_sale_considerations.map((w, i) => (
                <li key={i}>
                  Account {w.account_id} / {w.symbol} —{" "}
                  {w.avoided_wash_sale ? "avoided" : "violated"}
                  {w.impact ? ` (${w.impact})` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
